<template>
    <div>

        <div
            class="w-full flex flex-col justify-center"
        >

            <div v-if="$slots.default" class="font-semibold text-black">
                <slot></slot>
            </div>
            <div v-show="files.length" class="-mt-8 mb-8">

                <div
                    v-for="(file, index) in files"
                    :key="file.name + file.size + file.lastModified"
                >
                    <file
                        ref="files"
                        :dont-upload="dontUpload"
                        :file="file"
                        @cancel="removeFile(index, $event)"
                        @success="handleUploadedFile"
                    ></file>
                </div>


            </div>

            <div v-show="acceptFiles"
                 :class="{'bg-primary-5' : canDrop, 'h-60': !centerItems}"
                 class="relative flex flex-col items-center justify-center rounded py-12 border-2 border-primary-20 border-dashed hover:bg-primary-5 trans-ease group"
            >
                <div class="opacity-0 absolute offset-0 w-full h-full"
                     @dragend="handleDragStop"
                     @dragenter="handleDragEnter"
                     @dragleave="handleDragStop"
                     @dragover.prevent
                     @drop.prevent="dropHandler"
                >
                    <label :for="'file-' + _uid" class="absolute inset-0 cursor-pointer"></label>
                </div>

                <div class="flex items-center" :class="{'flex-col space-y-12' : centerItems}">

                    <div class="mr-12 flex items-center justify-center">
                        <svg-icon :class="[ canDrop ? 'text-secondary' : 'text-black group-hover:text-secondary']" class="w-18 h-18" type="upload"></svg-icon>
                    </div>

                    <div class="flex" :class="centerItems ? 'flex-col space-y-12 mt-12' : 'flex-wrap items-center'">
                        <div class="text-black mr-8">{{ $t('general.uploads.drop_to_upload') }} {{ $t('general.or') }}</div>

                       <div class="text-center inline-flex justify-center">
                           <simple-button
                               @click="$refs.file.click()"
                               display="secondary"
                               look="primary"
                               size="small"
                           >
                               {{ $t('general.uploads.click_to_choose') }}
                           </simple-button>
                       </div>
                    </div>
                </div>

            </div>

        </div>

        <input
            ref="file"
            :id="'file-' + _uid"
            :accept="onlyAccept ? onlyAccept : type === 'image' ? 'image/*' : ''"
            :multiple="multiple"
            class="hidden"
            type="file"
            @change="handleInput"
        >
    </div>
</template>

<script>
import File from './File.vue';
import SvgIcon from '../ui/SvgIcon.vue';
import SimpleButton from "../ui/SimpleButton.vue";

export default {
    components: {
        SimpleButton,
        'file': File,
        'svg-icon': SvgIcon,
    },
    props: {
        value: {
            // This can be either a string or an array (depending on if the fileUpload component
            // accepts multiple files or not).
            required: true,
        },
        multiple: Boolean,
        allData: Boolean,
        type: {
            type: String,
            default: 'document',
            validator: value => ['image', 'document'].includes(value),
        },
        withoutUpload: Boolean,
        centerItems: {type: Boolean, default: false},
        onlyAccept: { type: String, default: ''},
    },
    data: function () {
        return {
            canDrop: null,
            dropHandler: this.handleDragStop,
            files: [],
            dontUpload: this.withoutUpload,
        };
    },
    computed: {
        acceptFiles() {
            return this.multiple ? true : !this.files.length;
        },
    },
    methods: {
        handleDragEnter(e) {
            if (this.validateItems(e.dataTransfer.items)) {
                this.canDrop = true;
                this.enableDropHandler();
            } else {
                this.canDrop = false;
            }
        },
        handleDragStop() {
            this.canDrop = null;
            this.disableDropHandler();
        },
        handleDrop(e) {
            this.handleDragStop();
            this.initiateUpload(e.dataTransfer.files);
        },
        handleInput(e) {
            this.initiateUpload(e.target.files);
        },
        enableDropHandler() {
            this.dropHandler = this.handleDrop;
        },
        disableDropHandler() {
            this.dropHandler = this.handleDragStop;
        },
        validateItems(items) {
            if (!this.multiple && items.length !== 1) {
                return false;
            }

            if (this.type === 'image') {

                for (let i = 0; i < items.length; i++) {
                    if (!items[i].type.match(/image\/.+/)) {
                        return false;
                    }
                }
            }

            return true;
        },
        initiateUpload(files) {
            this.files.push(...Array.from(files));

            if (this.withoutUpload) {
                this.$emit('input', this.multiple ? this.files : this.files[0]);
            }
        },
        handleUploadedFile(file) {
            if (this.multiple) {
                this.$emit('input', this.value.concat([(this.allData ? file : file.id)]));
            } else {
                this.$emit('input', (this.allData ? file : file.id));
            }
        },
        removeFile(index, queuedFile) {

            if (queuedFile) {
                if (this.multiple) {
                    this.$emit('input', this.value.filter(value => value !== queuedFile.id));
                } else {
                    this.$emit('input', null);
                }
            }

            this.$emit('removed', {file: queuedFile, index, rawFile: this.files[index]});
            this.files.splice(index, 1);
        },
        // This method is used externally in case you manually handle the uploads
        removeFileByName(name) {
            this.files = this.files.filter(file => file.name !== name);
        },
        filesAreValid() {
            const files = this.$refs.files;

            if (!files) {
                return true;
            }

            if (!Array.isArray(files)) {
                return files.validFile();
            }

            return files.filter(f => !f.validFile()).length === 0;
        },
        setFiles(files) {
            this.dontUpload = true;
            this.files = files;

            if (!this.withoutUpload) {
                this.$nextTick(() => this.dontUpload = false);
            }
        }
    }
}
</script>
